import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import {
  DoneOutlineRounded,
  AccessTime,
  CalendarMonth,
  LocationOn,
} from "@mui/icons-material";
import axios from "axios";
import { getToken } from "auth/authenticate";
import { authHeader } from "auth/authHeader";

// =======================================================
// --- LOCAL UTILITIES (To resolve import errors) ---
// We rely on these definitions to make the component self-contained.
// The external imports for these files have been removed:
// "helpers/appointmentDataUtil", "auth/authHeader", "./global-components/MainButton", "./bookingServices"
// =======================================================

// Placeholder for authHeader utility (assuming a token is in localStorage)

// Placeholder for MainButton
const MainButton = ({ onClick, btnLabel, children, ...props }) => (
  <button
    onClick={onClick}
    style={{
      backgroundColor: "#1976d2",
      color: "white",
      padding: "10px 20px",
      borderRadius: "4px",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      fontWeight: "bold",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.3s",
      width: "100%",
      ...props.sx,
    }}
    disabled={props.disabled}
  >
    {children}
    {btnLabel}
  </button>
);

/**
 * Utility function to guess the user's current timezone using native JS.
 */
const guessTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/**
 * Converts a UTC date/time string to a user's local timezone string for display.
 * @param {string} utcDateString - The appointment date and time in UTC (e.g., '2025-11-20T14:30:00Z').
 * @param {string} localTimezone - The user's local timezone string.
 * @returns {{time: string, timezoneShort: string}}
 */
const convertUTCToLocalDisplayTime = (utcDateString, localTimezone) => {
  if (!utcDateString || !localTimezone)
    return { time: "N/A", timezoneShort: "" };

  try {
    // Append 'Z' to treat the date string as UTC
    const date = new Date(
      utcDateString.endsWith("Z") ? utcDateString : `${utcDateString}Z`
    );

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: localTimezone,
      hour12: true,
    });
    const timePart = timeFormatter.format(date);

    const tzShortFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: localTimezone,
      timeZoneName: "short",
    });
    const timezoneShort = tzShortFormatter.format(date).split(" ").pop();

    return { time: timePart, timezoneShort };
  } catch (e) {
    return { time: "N/A (Error)", timezoneShort: "" };
  }
};
// =======================================================
// --- END: LOCAL UTILITIES ---
// =======================================================

const BookingComplete = () => {
  // appointmentId is correctly extracted from the URL route parameter
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  // appointmentData is now derived ONLY from the full API response
  const [appointmentData, setAppointmentData] = useState(null);
  const [fullAppointmentDetails, setFullAppointmentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);

  // === Temporary Password Display (if guest) ===
  const [tempPassword, setTempPassword] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const timeZone = guessTimezone();

  const getProviderDisplayName = (doctor = {}) => {
  if (!doctor) return "Unknown health professional";

  return (
    doctor.displayName ||
    [doctor.prefix, doctor.name].filter(Boolean).join(" ") ||
    doctor.name ||
    "Unknown health professional"
  );
};

  /**
   * Fetches the full appointment details from the backend, solely using the URL ID.
   * @param {string} id - The appointment ID (from URL).
   */
  const fetchFullAppointmentDetails = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}appointment/${id}`,
        { headers: authHeader() }
      );

      const fullData = response.data?.data;
      // Handle cases where the API might wrap the data in an array
      const finalData = Array.isArray(fullData) ? fullData[0] : fullData;

      if (finalData) {
        setFullAppointmentDetails(finalData);

        // Populate display state directly from the authoritative API response

        const fullUtcTimestamp = finalData.slotTime?.includes("T")
          ? finalData.slotTime // already a full UTC datetime
          : `${finalData.date}T${finalData.slotTime?.replace("Z", "")}Z`;

        setAppointmentData({
          appointmentId: finalData.id || finalData.appointmentId,
          // doctorName: finalData.Doctor?.name,
            doctorName: getProviderDisplayName(finalData.Doctor),
          appointmentDateUTC: finalData.date,
          appointmentTimeUTC: fullUtcTimestamp, // ✅ keep raw UTC datetime string
          localTimezone: timeZone,
          patientName: finalData.Patient?.name,
          patientEmail: finalData.Patient?.User?.email,
          profession: finalData.Doctor?.professionType || "Health professional",
        });

        setErrorState(null);
      } else {
        throw new Error("Appointment data not found in response.");
      }
    } catch (error) {
      console.error("Failed to fetch appointment data:", error);
      setErrorState(
        "Could not load appointment details. Please ensure the ID is correct and you are logged in."
      );
      setAppointmentData(null); // Clear any old data
      setFullAppointmentDetails(null);
    }
  };

  useEffect(() => {
    const loadAppointmentData = async () => {
      setIsLoading(true);

      if (appointmentId) {
        // Rely *only* on the API call with the URL parameter
        await fetchFullAppointmentDetails(appointmentId);
      } else {
        console.error("No appointmentId found in URL.");
        setErrorState(
          "Cannot confirm appointment: Missing appointment ID in URL."
        );
      }

      setIsLoading(false);
    };

    loadAppointmentData();
  }, [appointmentId, timeZone]);

  // --- Load password from localStorage if available ---
  useEffect(() => {
    try {
      const bookingData = JSON.parse(localStorage.getItem("bookingData"));
      if (bookingData?.tempPassword) {
        setTempPassword(bookingData.tempPassword);
        // console.log("Temporary password loaded from localStorage.");
      } else {
        // console.log("ℹNo temporary password found in bookingData.");
      }
    } catch (e) {
      console.warn("Failed to parse bookingData:", e);
    }
  }, []);

  // --- Cleanup bookingData after 30 seconds ---
  useEffect(() => {
    if (!tempPassword) return; // only if password existed

    const timer = setTimeout(() => {
      try {
        localStorage.removeItem("bookingData");
        // console.log("🧹 Cleared bookingData after 30 seconds.");
        setTempPassword(null); // remove from UI too
      } catch (e) {
        console.warn("Failed to clear bookingData:", e);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [tempPassword]);

  const handleProceedToConsultation = () => {
    if (!fullAppointmentDetails) {
      console.error("Missing appointment details for navigation");
      return;
    }

    // Safely extract name parts
    const patientName = fullAppointmentDetails.Patient?.name || "";
    const nameParts = patientName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    

    // Prepare state for navigation as requested in your original logic
    navigate("/apt-external/join-consultation", {
      state: {
        secretData: {
          appointmentData: fullAppointmentDetails,
          userData: {
            firstName: firstName,
            lastName: lastName,
            email: fullAppointmentDetails.Patient?.User?.email,
            User: {
              timezone: timeZone,
              phoneNumber: fullAppointmentDetails.Patient?.User?.phoneNumber,
            },
          },
        },
      },
    });
  };

  // Time conversion utility now uses the data fetched from the API
  const displayTimeDetails = appointmentData?.appointmentTimeUTC
    ? convertUTCToLocalDisplayTime(appointmentData.appointmentTimeUTC, timeZone)
    : { time: "N/A", timezoneShort: "" };

  const displayTime = displayTimeDetails.time;
  const displayTimezone = displayTimeDetails.timezoneShort;
  const displayDate = appointmentData?.appointmentDateUTC || "N/A";



  // Display Loader or Error if data is not ready
  if (isLoading || !appointmentData) {
    return (
      <Paper
        elevation={3}
        sx={{ padding: 3, maxWidth: 600, margin: "40px auto" }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          p={3}
        >
          {isLoading && <CircularProgress />}
          <Typography mt={2} color={errorState ? "error" : "textPrimary"}>
            {errorState || "Confirming Appointment Details..."}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Use the logic from your original JSX structure for the final display
  return (
    <Paper
      elevation={3}
      sx={{ padding: 3, maxWidth: 600, margin: "40px auto" }}
      className="payment-complete-container"
    >
      <div
        className="prompt_modal_heading"
        style={{ display: "flex", alignItems: "center" }}
      >
        <DoneOutlineRounded
          sx={{
            color: "rgb(4, 206, 122)",
            border: "1px solid rgb(4, 206, 122)",
            borderRadius: "50%",
            padding: "5px",
            marginRight: 2,
            minWidth: 35,
            minHeight: 35,
          }}
        />
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "normal", color: "#2d3436" }}
          >
            Appointment Confirmed
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Payment was successful
          </Typography>
        </Box>
      </div>

      <div className="prompt_modal_summary_ctx" style={{ marginTop: "20px" }}>
        {/* Appointment Details */}
        <Box className="appointment-detail-item" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Appointment ID: <strong>{appointmentData.appointmentId}</strong>
          </Typography>
        </Box>

        <Box className="appointment-detail-item" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {/* Doctor: <strong>Dr. {appointmentData.doctorName}</strong> */}
             Provider: <strong>{appointmentData.doctorName}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
  Profession : {appointmentData.profession}
</Typography>
          {fullAppointmentDetails?.Doctor?.qualifications && (
            <Typography variant="body2" color="textSecondary">
              Qualifications : {fullAppointmentDetails.Doctor.qualifications.join(", ")}
            </Typography>
          )}
        </Box>

        <Box
          className="appointment-detail-item"
          sx={{ borderTop: "1px solid #eee", pt: 2 }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarMonth color="primary" />
            <Typography variant="body1">
              Date: <strong>{displayDate}</strong>
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTime color="primary" />
            <Typography variant="body1">
              Time: <strong>{displayTime}</strong> in{" "}
              <strong>{displayTimezone}</strong> timezone
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn color="primary" />
            <Typography variant="body1">Online Consultation</Typography>
          </Box>
        </Box>

        <Box
          className="appointment-detail-item"
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "rgba(25, 118, 210, 0.05)",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="textSecondary" fontWeight="bold">
            Before your consultation, please ensure you have:
          </Typography>
          <ul
            style={{
              paddingLeft: "20px",
              marginTop: "5px",
              fontSize: "0.9rem",
            }}
          >
            <li>A stable internet connection</li>
            <li>A quiet environment</li>
            <li>Your camera and microphone ready</li>
            <li>Any relevant medical records</li>
          </ul>
        </Box>
      </div>

      {tempPassword && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "rgba(255, 213, 79, 0.1)",
            border: "1px dashed #fbc02d",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            color="warning.main"
            fontWeight="bold"
            mb={1}
          >
            Your Account Password (Please Save It)
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              letterSpacing: "1px",
              color: "#f57f17",
              mb: 1,
              fontSize: "1.1rem",
            }}
          >
            {tempPassword}
          </Typography>

          <MainButton
            btnLabel={isCopied ? "Copied!" : "Copy Password"}
            onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              setIsCopied(true);
              setTimeout(() => setIsCopied(false), 2000); // reset after 2s
              alert("Password copied to clipboard!");
            }}
            sx={{
              backgroundColor: "#fbc02d",
              color: "#000",
              width: "fit-content",
              fontWeight: "bold",
            }}
          />
        </Box>
      )}
      <div className="prompt_modal_cta_btns" style={{ marginTop: "20px" }}>
        <MainButton
          onClick={handleProceedToConsultation}
          btnLabel={" Proceed to Waiting Room"}
          sx={{ maxWidth: "400px", margin: "0 auto" }}
        />
      </div>
    </Paper>
  );
};

export default BookingComplete;
